import React, { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import {
  Autocomplete,
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Snackbar,
  Alert,
  Card,
  CardHeader,
  CardContent,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup as MuiRadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Radio as MuiRadio,
  MenuItem,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { DeleteOutline, Add, Remove } from '@mui/icons-material';
import { useCart, extractQuoteGroups } from '../../../../CartContext';
import {
  createOrder,
  deleteAllOrder,
  deleteDraftCheckOutItem,
  getDraftCheckOutItems,
  updateDraftCheckOutItems,
  getBottleLabLocations,
  addInternalUserAddress,
  createInternalUserOrder,
  addAddress,
  getInternalUserDespatchLocations,
  getAllDraftCheckOutListApi,
  getInternalUserContacts,
  uploadOrderAttachments,
  getClientInfo
} from 'services';
import { DateInputField } from 'components';
import { useNavigate } from 'react-router';
import ContainerImage from 'assets/Container_Image.png';
import { useSearchParams } from 'react-router';
import CircularProgress from '@mui/material/CircularProgress';
import useFetch from '../../../customHooks/useFetch';

const createInitialRecipient = (emailId = '', name = '', contactName = '') => ({
  name,
  emailId,
  contactName,
  contactNo: '',
  date: '',
  street: '',
  suburb: '',
  state: '',
  postalCode: '',
  country: 'Australia',
  pickupLocation: '',
  pickUpLocationID: '',
  instructions: '',
  deliveryInstruction: '',
  saveForFuture: false,
  isDefault: false,
  despatchLocationID: ''
});

const normalizeDraftCheckoutResponse = (data: any) => {
  const draftItems = Array.isArray(data?.draftItems)
    ? data.draftItems
    : [];

  const draftBottleGroups = Array.isArray(data?.draftBottleGroups)
    ? data.draftBottleGroups
    : data?.draftBottleGroup &&
      Array.isArray(data.draftBottleGroup.draftItems)
      ? [data.draftBottleGroup]
      : [];

  const groupedDraftItems = draftBottleGroups.flatMap((group: any) => {
    const groupedItems = Array.isArray(group?.draftItems)
      ? group.draftItems
      : [];

    return groupedItems.map((item: any, index: number) => ({
      ...item,

      draftBottleGroupID: group.id ?? group.draftGroupID ?? null,
      draftBottleGroupName: group.name || '',
      draftBottleGroupSize: group.size ?? '',
      draftBottleGroupClientID:
        group.clientID ?? group.clientId ?? null,
      draftBottleGroupOrder: index,
      quoteAnalysisRefID: group.quoteAnalysisRefID ?? null,
      quoteAnalysisRefComments:
        group.quoteAnalysisRefComments ?? '',

      // Quote information
      quoteNumber: data?.quoteNumber ?? '',
      quoteId: data?.quoteId ?? null,
    }));
  });

  // Add quote information to normal draft items too
  const normalizedDraftItems = draftItems.map((item: any) => ({
    ...item
  }));

  return [...normalizedDraftItems, ...groupedDraftItems];
};

const stripDraftBottleGroupMeta = (item: any) => {
  const {
    draftBottleGroupID,
    draftBottleGroupName,
    draftBottleGroupSize,
    draftBottleGroupClientID,
    draftBottleGroupOrder,
    ...cleanItem
  } = item;

  return cleanItem;
};

const ALLOWED_EXTENSIONS = new Set([
  '.pdf', '.xls', '.xlsx', '.doc', '.docx',
  '.csv', '.msg', '.eml',
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff'
]);

const ALLOWED_ACCEPT = '.pdf,.xls,.xlsx,.doc,.docx,.csv,.msg,.eml,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff';

const MAX_TOTAL_SIZE = 2 * 1024 * 1024; // 2 MB total across all files

const FIELD_LIMITS = {
  orderName: 100,
  recipientName: 200,
  contactName: 100,
  email: 100,
  contactNo: 20,
  postalCode: 10,
  suburb: 100,
  street: 100,
  instructions: 300,
  deliveryInstruction: 150
};

const exceedsMaxLength = (value: any, maxLength: number) => `${value ?? ''}`.length > maxLength;
const maxLengthMessage = (maxLength: number) => `Maximum ${maxLength} characters allowed`;
const characterCountText = (value: any, maxLength: number) => `${`${value ?? ''}`.length}/${maxLength} characters`;

const isAllowedFileType = (file: File): boolean => {
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  return ALLOWED_EXTENSIONS.has(ext);
};

const steps = [
  'Order Summary',
  'Order Type & Contact Details',
  'Confirmation'
];

interface CheckoutPageProps {
  cart: any[];
  onSubmit: (order: any) => void;
  onClose?: () => void;
  getAllState: any[];
  addressList: any[];
  getUserAddressApi: () => void;
}

export default function CheckoutPage({
  onSubmit,
  onClose,
  getAllState,
  addressList,
  getUserAddressApi
}: CheckoutPageProps) {
  const jwt: any = localStorage.getItem('token');
  const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;
  const isExternalUser = labUserCheck?.UserType === 'External';
  const defaultRecipientEmail = isExternalUser ? (labUserCheck?.Email || '') : '';
  const defaultRecipientContactName = isExternalUser ? (labUserCheck?.given_name || '') : '';
  const defaultRecipientContactNo = isExternalUser ? '' : undefined;
  const getRecipientEmail = (addressEmail = '') => defaultRecipientEmail || addressEmail;
  const getRecipientContactNo = (addressContactNo = '') => defaultRecipientContactNo ?? addressContactNo;
  const getContactOptionLabel = (contact: any) => {
    if (typeof contact === 'string') {
      return contact;
    }

    return contact?.userName || contact?.contactName || contact?.name || contact?.fullName || contact?.addedBy || '';
  };
  const getContactEmail = (contact: any) => contact?.emailAddress || contact?.email || contact?.emailId || '';
  const getContactNumberFields = (source: any) => {
    if (isExternalUser) {
      return {
        contactNo: ''
      };
    }

    const mobileNumber = `${source?.mobileNumber ?? source?.mobile ?? ''}`.trim();

    return {
      contactNo: getRecipientContactNo(mobileNumber)
    };
  };
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  const navigate = useNavigate();
  const [userInfo] = useFetch();
  const { cart, setCart, selectedClientId, selectedClientName, setSelectedClientId, updateClientSelected, eskiesToFit, setEskiesToFit, quoteAnalysisRefID, quoteGroups, setQuoteGroups } = useCart();
  const matchedExternalUserMap = useMemo(() => {
    const userMaps = Array.isArray(userInfo?.userMap) ? userInfo.userMap : [];
    const resolvedClientId = clientId ? Number(clientId) : selectedClientId;

    if (resolvedClientId) {
      return userMaps.find((item: any) => String(item?.clientID) === String(resolvedClientId)) || null;
    }

    return userMaps[0] || null;
  }, [clientId, userInfo?.userMap, selectedClientId]);
  const defaultRecipientName = isExternalUser
    ? (matchedExternalUserMap?.clientName || '')
    : (selectedClientName || '');
  const initialRecipient = createInitialRecipient(
    defaultRecipientEmail,
    defaultRecipientName,
    defaultRecipientContactName
  );

  const [activeStep, setActiveStep] = useState(0);
  const [orderName, setOrderName] = useState('');
  const [searchContactName, setSearchContactName] = useState('');
  const [mode, setMode] = useState<'pickup' | 'delivery'>('pickup');
  const [recipient, setRecipient] = useState(initialRecipient);
  const totalItems = cart.reduce((sum, item) => sum + (Number(item.noOfUnits) || 0), 0);
  const hasPackageInCart = cart.some(item => item?.isPackage === true);
  const hasCheckoutItems = cart.length > 0 || quoteGroups.length > 0;
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [acknowledgedStatus, setAcknowledgedStatus] = useState(false);
  const [acknowledgeResponse, setAcknowledgeResponse] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogMode, setDeleteDialogMode] = useState<'single' | 'all'>('single');
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [modifiedQuantities, setModifiedQuantities] = useState<Record<string, number | ''>>({});
  const [cartMultipliers, setCartMultipliers] = useState<Record<string, number>>({});

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<any>();
  const [defaultAddressId, setDefaultAddressId] = useState('');
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
  const [localAddressList, setLocalAddressList] = useState<any[]>(addressList);
  const [dispatchLocations, setDispatchLocations] = useState<any[]>([]);
  const [draftCheckoutItems, setDraftCheckoutItems] = useState<any[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [showQualityPackagePrompt, setShowQualityPackagePrompt] = useState(true);
  const [hasDeclinedQualityPackagePrompt, setHasDeclinedQualityPackagePrompt] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previousDefaultRecipientName = useRef(defaultRecipientName);

  const [dateError, setDateError] = useState('');
  const [labLocations, setLabLocations] = useState<any[]>([]);
  const [internalContacts, setInternalContacts] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedContact, setSelectedContact] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactNoError, setContactNoError] = useState('');
  const [draftedDeleteDialogOpen, setDraftedDeleteDialogOpen] = useState(false);
  const [clientInformation, setClientInformation] = useState<any>(null);

  const [quoteId, setQuoteId] = useState<number | null>(null);

  useEffect(() => {
    let isCurrentClient = true;
    setOrderName('');
    setQuoteId(null);

    if (!selectedClientId || selectedClientId === -1) {
      return;
    }

    const loadQuoteDetails = async () => {
      try {
        const response = await getDraftCheckOutItems(selectedClientId);
        if (isCurrentClient && response?.status === 200) {
          setOrderName((currentName) => currentName || `${response.data?.quoteNumber ?? ''}`);
          setQuoteId(response.data?.quoteId ?? null);
          setQuoteGroups(extractQuoteGroups(response.data));
        }
      } catch (error) {
        return;
      }
    };

    loadQuoteDetails();

    return () => {
      isCurrentClient = false;
    };
  }, [selectedClientId]);

  const latestSearchRef = useRef('');

  const orderSummarySections = useMemo(() => {
    const ungroupedItems: any[] = [];
    const groupedItemsMap = new Map<string, { key: string; id: string; name: string; size: number | string; items: any[]; quoteAnalysisRefID: string | null; quoteAnalysisRefComments: string }>();

    cart.forEach((item) => {
      const groupName = `${item?.draftBottleGroupName ?? ''}`.trim();

      if (!groupName) {
        ungroupedItems.push(item);
        return;
      }

      const groupKey = `${groupName}__${item?.draftBottleGroupSize ?? ''}`;

      if (!groupedItemsMap.has(groupKey)) {
        groupedItemsMap.set(groupKey, {
          key: groupKey,
          id: item?.draftGroupID ?? '',
          name: groupName,
          size: item?.draftBottleGroupSize ?? '',
          items: [],
          quoteAnalysisRefID: item?.quoteAnalysisRefID ?? null,
          quoteAnalysisRefComments: item?.quoteAnalysisRefComments ?? '',
        });
      }

      groupedItemsMap.get(groupKey)?.items.push(item);
    });

    // Quote groups without draft items are not part of the cart, so add them from the response
    quoteGroups.forEach((group: any) => {
      const groupKey = `${group.name}__${group.size ?? ''}`;
      const existingGroup = groupedItemsMap.get(groupKey);

      if (existingGroup) {
        existingGroup.id = existingGroup.id || group.id;
        return;
      }

      groupedItemsMap.set(groupKey, {
        key: groupKey,
        id: group.id,
        name: group.name,
        size: group.size ?? '',
        items: [],
        quoteAnalysisRefID: group.quoteAnalysisRefID,
        quoteAnalysisRefComments: group.quoteAnalysisRefComments ?? ''
      });
    });

    const groupedItems = Array.from(groupedItemsMap.values());

    return {
      ungroupedItems,
      quoteGroups: groupedItems.filter((group) => group.quoteAnalysisRefID != null),
      groupedItems: groupedItems.filter((group) => group.quoteAnalysisRefID == null)
    };
  }, [cart, quoteGroups]);

  const hasRegularOrderItems = orderSummarySections.ungroupedItems.length > 0 ||
    orderSummarySections.groupedItems.length > 0;

  const getCartItemLabel = (item: any) => {
    return `${item.containerName || item.name || ''}${item.containerDesc ? ` (${item.containerDesc})` : ''}`.trim();
  };

  const getMultiplier = (item: any) =>
    cartMultipliers[item.id] ?? item.multiplier ?? 1;

  const handleCartMultiplierChange = (itemId: any, value: number) => {
    const clampedValue = Math.max(1, value);

    setCartMultipliers((prev) => ({
      ...prev,
      [itemId]: clampedValue,
    }));

    // keep cart in sync so confirmation step and order submission use the latest multiplier
    setCart(cart.map((cartItem) =>
      cartItem.id === itemId ? { ...cartItem, multiplier: clampedValue } : cartItem
    ));
  };

  const getSelectedContactNumber = (recipientData: any = recipient) => `${recipientData?.contactNo ?? ''}`.trim();

  const handleContactNumberChange = (value: string) => {
    setRecipient(prev => ({ ...prev, contactNo: value }));
  };

  const handleInternalContactSelect = (contact: any) => {
    const contactName = getContactOptionLabel(contact);

    setRecipient(prev => ({
      ...prev,
      contactName,
      emailId: getContactEmail(contact),
      ...getContactNumberFields(contact)
    }));
    setSearchContactName(contactName);
  };

  const validateDate = (selectedDate: string) => {
    if (!selectedDate) {
      setDateError('Required Date is required');
      return false;
    }

    const selected = moment(selectedDate, 'YYYY-MM-DD', true);

    if (!selected.isValid()) {
      setDateError('Please enter a valid date');
      return false;
    }

    if (selected.isBefore(moment(), 'day')) {
      setDateError('Please select today\'s date or a future date');
      return false;
    }

    if (labUserCheck?.UserType !== 'Internal' && selected.isBefore(moment(getRequiredDateCutoffMin(), 'YYYY-MM-DD'), 'day')) {
      setDateError('Orders placed after 5 PM require the day after next as the Required Date');
      return false;
    }

    setDateError('');
    return true;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Handle pickup location change - auto-populate address fields
    if (name === 'pickupLocation' && isPickup) {
      const selectedLocation = labLocations.find(loc => loc.name === value);
      if (selectedLocation) {
        let stateIdValue = selectedLocation.stateId;
        if (!stateIdValue && selectedLocation.state) {
          const stateObj = getAllState.find(s => s.name === selectedLocation.state);
          stateIdValue = stateObj?.name || '';
        }

        setRecipient(prev => ({
          ...prev,
          [name]: value,
          pickUpLocationID: selectedLocation.id || '',
          suburb: selectedLocation.suburb || '',
          state: stateIdValue || '',
          street: selectedLocation.street || '',
          postalCode: selectedLocation.postalCode || '',
          country: selectedLocation.country || 'Australia'
        }));
        return;
      }
    }

    if (name === 'date') {
      const day = new Date(value).getDay();

      if (day === 0 || day === 6) {
        setDateError('Saturday and Sunday are not allowed');
        return;
      }

      validateDate(value);
    }

    setRecipient(prev => ({ ...prev, [name]: value }));
  };

  const isDelivery = mode === 'delivery';
  const isPickup = mode === 'pickup';
  const currentContactNumber = getSelectedContactNumber();
  const hasCharacterLimitErrors =
    exceedsMaxLength(orderName, FIELD_LIMITS.orderName) ||
    exceedsMaxLength(recipient.name, FIELD_LIMITS.recipientName) ||
    exceedsMaxLength(recipient.contactName, FIELD_LIMITS.contactName) ||
    exceedsMaxLength(recipient.emailId, FIELD_LIMITS.email) ||
    exceedsMaxLength(currentContactNumber, FIELD_LIMITS.contactNo) ||
    exceedsMaxLength(recipient.postalCode, FIELD_LIMITS.postalCode) ||
    exceedsMaxLength(recipient.suburb, FIELD_LIMITS.suburb) ||
    exceedsMaxLength(recipient.street, FIELD_LIMITS.street) ||
    exceedsMaxLength(recipient.instructions, FIELD_LIMITS.instructions) ||
    exceedsMaxLength(recipient.deliveryInstruction, FIELD_LIMITS.deliveryInstruction);

  useEffect(() => {
    const trimmedDefaultName = `${defaultRecipientName ?? ''}`.trim();

    if (!trimmedDefaultName) {
      previousDefaultRecipientName.current = '';
      return;
    }

    setRecipient((prev) => {
      const currentName = `${prev?.name ?? ''}`.trim();

      if (!currentName || currentName === previousDefaultRecipientName.current) {
        previousDefaultRecipientName.current = trimmedDefaultName;
        return {
          ...prev,
          name: trimmedDefaultName
        };
      }

      previousDefaultRecipientName.current = trimmedDefaultName;
      return prev;
    });
  }, [defaultRecipientName]);

  useEffect(() => {
    if (isPickup) {
      const fetchLabLocations = async () => {
        try {
          const response = await getBottleLabLocations();
          if (response?.status === 200) {
            setLabLocations(response.data);
          }
        } catch (error) {
          // console.error('Error fetching lab locations:', error);
        }
      };
      fetchLabLocations();
    }
  }, [isPickup]);

  useEffect(() => {
    if (selectedClientId !== null && selectedClientId !== undefined) {
      getClientInformation(selectedClientId);
    }
  }, [selectedClientId]);

  const getClientInformation = async (selectedClientId: any) => {
    try {
      const response = await getClientInfo(selectedClientId);
      if (response?.status === 200) {
        // Handle the client information as needed
        setClientInformation(response.data);
      }
    } catch (error) {
      // console.error('Error fetching client information:', error);
    }
  };

  const isInternalDeliveryUser = labUserCheck?.UserType !== 'External';

  const hasBaseDeliveryMandatoryFields =
    `${recipient.name ?? ''}`.trim() !== '' &&
    `${recipient.emailId ?? ''}`.trim() !== '' &&
    currentContactNumber !== '' &&
    `${recipient.date ?? ''}`.trim() !== '' &&
    `${recipient.street ?? ''}`.trim() !== '' &&
    `${recipient.suburb ?? ''}`.trim() !== '' &&
    `${recipient.state ?? ''}`.trim() !== '' &&
    `${recipient.postalCode ?? ''}`.trim() !== '' &&
    `${recipient.country ?? ''}`.trim() !== '' &&
    !dateError;

  const hasInternalOnlyDeliveryMandatoryFields =
    `${recipient.contactName ?? ''}`.trim() !== '' &&
    `${recipient.despatchLocationID ?? ''}`.trim() !== '';

  const isDeliveryMandatoryValid =
    hasBaseDeliveryMandatoryFields &&
    (!isInternalDeliveryUser || hasInternalOnlyDeliveryMandatoryFields);

  const hasRequiredContactName = !isExternalUser || `${recipient.contactName ?? ''}`.trim() !== '';

  const isRecipientValid = hasRequiredContactName && (isDelivery
    ? isDeliveryMandatoryValid
    : isPickup
      ? (
        `${recipient.name ?? ''}`.trim() !== '' &&
        `${recipient.emailId ?? ''}`.trim() !== '' &&
        currentContactNumber !== '' &&
        `${recipient.date ?? ''}`.trim() !== '' &&
        `${recipient.pickupLocation ?? ''}`.trim() !== '' &&
        `${recipient.pickUpLocationID ?? ''}`.trim() !== '' &&
        !dateError
      )
        : true);

  const isEmailValid = !recipient.emailId || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recipient.emailId);
  const isConfirmDisabled = !isRecipientValid || !hasCheckoutItems || !isEmailValid || !!dateError || hasCharacterLimitErrors;

  const handleModeChange = (_: any, value: string | null) => {
    if (value && (value === 'pickup' || value === 'delivery')) {
      setMode(value as 'pickup' | 'delivery');
      setRecipient(initialRecipient);
      setSearchContactName('');
      setIsAddingNewAddress(false);
      setContactNoError('');

      if (!recipient.date) {
        setDateError('ExpeRequiredcted Date is required');
      } else {
        validateDate(recipient.date);
      }
    }
  };

  const handleDeleteItem = (item: any) => {
    setItemToDelete(item);
    setDeleteDialogMode('single');
    setDeleteDialogOpen(true);
  };

  const handleDraftedDeleteItem = (item: any) => {
    setItemToDelete(item);
    setDraftedDeleteDialogOpen(true);
  };

  const handleDeleteAllDialog = () => {
    setDeleteDialogMode('all');
    setDeleteDialogOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (deleteDialogMode === 'all') {
      setDeleteDialogOpen(false);
      await handleDeleteAllApi(selectedClientId, 'all');
      setItemToDelete(null);
      return;
    }
    if (!itemToDelete) return;
    try {
      const response = await deleteDraftCheckOutItem(itemToDelete, selectedClientId);
      if (response.status === 200) {
        // Filter cart locally to preserve modified quantities
        const updatedCart = cart.filter(item =>
          itemToDelete.items?.length
            ? item.draftGroupID !== itemToDelete.id
            : item.id !== itemToDelete.id
        );

        setCart(updatedCart);
        setSnackbar({
          open: true,
          message: 'Item removed from cart successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data || 'Failed to remove item from cart',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to remove item from cart',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleQuantityChange = async (item: any, newQuantity: number | "") => {
    try {

      const newCart = cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, noOfUnits: newQuantity }
          : cartItem
      );

      setCart(newCart);

      setModifiedQuantities(prev => ({
        ...prev,
        [item.id]: newQuantity
      }));

    } catch (error) {
      await refreshCartItemsWithQuantities();
    }
  };

  const refreshCartItemsWithQuantities = async () => {
    try {
      const response = await getDraftCheckOutItems(selectedClientId);

      if (response.status === 200) {
        const updatedCart = normalizeDraftCheckoutResponse(
          response?.data
        )?.map((item: any) => ({
          ...item,
          noOfUnits: Object.prototype.hasOwnProperty.call(
            modifiedQuantities,
            item.id
          )
            ? modifiedQuantities[item.id]
            : item.noOfUnits,
        }));

        setCart(updatedCart);
        setQuoteGroups(extractQuoteGroups(response?.data));
      }
    } catch (error) {
      // console.error('Error refreshing cart:', error);
    }
  };

  const updateCartQuantities = async (item: any) => {
    try {
      const modifiedQty = modifiedQuantities[item.id];

      const updatedNoOfUnits =
        modifiedQty ?? item.noOfUnits;

      // prevent empty update
      if (`${updatedNoOfUnits ?? ''}`.trim() === '') {
        return;
      }

      const payload = {
        ...stripDraftBottleGroupMeta(item),
        noOfUnits: updatedNoOfUnits
      };

      await updateDraftCheckOutItems({
        draftItem: payload
      });

      // cleanup after success
      setModifiedQuantities((prev: any) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });

    } catch (error) {
      // console.error('Update error:', error);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const hasEmptyNoOfUnits = cart.some(item => `${item.noOfUnits ?? ''}`.trim() === '');
  const canProceedToStep2 = hasCheckoutItems && !hasEmptyNoOfUnits && orderName.trim() !== '' && !exceedsMaxLength(orderName, FIELD_LIMITS.orderName) && (hasPackageInCart || hasDeclinedQualityPackagePrompt);
  const hasEmailValue = `${recipient.emailId ?? ''}`.trim() !== '';
  // If adding new address, require all address fields to be filled
  const isNewAddressValid = isAddingNewAddress
    ? (
      recipient.name &&
      recipient.emailId &&
      currentContactNumber &&
      recipient.date &&
      recipient.state &&
      recipient.street &&
      recipient.suburb &&
      recipient.postalCode &&
      recipient.country &&
      (isExternalUser || recipient.contactName) &&
      !dateError &&
      isEmailValid
    )
    : true;

  const canProceedToStep3 =
    isRecipientValid &&
    isEmailValid &&
    hasEmailValue &&
    !dateError &&
    !hasCharacterLimitErrors &&
    isNewAddressValid;

  useEffect(() => {
    if (isDelivery) {
      getUserAddressApi();
      if (labUserCheck?.UserType !== 'External') {
        getDespatchLocations();
      }
    }
  }, [mode]);

  useEffect(() => {
    if (localAddressList.length > 0) {
      const defaultAddr = localAddressList.find(addr => addr.isDefault === true);
      if (defaultAddr) {
        setDefaultAddressId(defaultAddr.id);
        setSelectedAddress(defaultAddr.id);
      } else if (localAddressList.length > 0) {
        setDefaultAddressId(localAddressList[0].id);
        setSelectedAddress(localAddressList[0].id);
      }
    }
  }, [localAddressList]);

  useEffect(() => {
    // When selectedClientId changes, clear address list if falsy, else assign new value
    if (!selectedClientId) {
      setLocalAddressList([]);
    } else {
      setLocalAddressList(addressList);
    }
  }, [selectedClientId, addressList]);

  useEffect(() => {
    if (editDialogOpen && defaultAddressId) {
      setSelectedAddress(defaultAddressId);
    }
  }, [editDialogOpen, defaultAddressId]);

  useEffect(() => {
    if (mode === 'delivery' && defaultAddressId && addressList.length > 0) {
      const addr = addressList.find(a => a.id === defaultAddressId);
      if (addr) {
        const newRecipient = {
          name: addr.name || '',
          emailId: getRecipientEmail(addr.emailId || addr.email || ''),
          contactName: defaultRecipientContactName,
          ...getContactNumberFields(addr),
          date: addr.date || '',
          suburb: addr.suburb || '',
          state: addr.state || '',
          street: addr.street || '',
          postalCode: addr.postalCode || '',
          country: addr.country || 'Australia',
          pickupLocation: addr.pickupLocation || '',
          pickUpLocationID: addr.pickUpLocationID || '',
          instructions: addr.instructions || '',
          deliveryInstruction: addr.deliveryInstruction || '',
          saveForFuture: false,
          isDefault: addr.isDefault || false,
          despatchLocationID: addr.despatchLocationID || ''
        };

        setRecipient(newRecipient);
        if (newRecipient.date) {
          validateDate(newRecipient.date);
        } else {
          setDateError('Required Date is required');
        }
      }
    }
  }, [mode, defaultAddressId, addressList]);

  useEffect(() => {
    if (!recipient.date) {
      setDateError('Required Date is required');
    } else {
      validateDate(recipient.date);
    }
  }, [mode]);

  useEffect(() => {
    if (isExternalUser || !selectedClientId || selectedClientId === -1) {
      setInternalContacts([]);
      return;
    }

    const searchValue = searchContactName.trim();

    latestSearchRef.current = searchValue;

    const timer = setTimeout(() => {
      getInternalUserContactName(
        selectedClientId,
        searchValue
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [isExternalUser, selectedClientId, searchContactName]);

  const getInternalUserContactName = async (
    clientId: number,
    searchName: string
  ) => {
    latestSearchRef.current = searchName;

    setLoading(true);

    try {
      const res = await getInternalUserContacts(
        clientId,
        searchName
      );

      if (latestSearchRef.current !== searchName) {
        return;
      }

      if (res?.status === 200) {
        setInternalContacts([...res.data]);
      } else {
        setInternalContacts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadAttachmentsForOrder = async (orderId: number) => {
    if (!attachments.length || !orderId) {
      return true;
    }

    try {
      const formData = new FormData();
      formData.append('orderId', `${orderId}`);
      attachments.forEach((file) => {
        formData.append('files', file);
      });

      const response = await uploadOrderAttachments(formData);

      if (response?.status !== 200) {
        setSnackbar({
          open: true,
          message: response?.data || 'Order was placed, but attachments failed to upload.',
          severity: 'error'
        });
        return false;
      }

      return true;
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data || error?.message || 'Order was placed, but attachments failed to upload.',
        severity: 'error'
      });
      return false;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const groupedItemsMap = new Map<string, { name: string; requestedQuantity: number; orderedContainers: any[]; quoteAnalysisRefID?: number | null }>();
      const topLevelItems = cart.filter(item => {
        const groupName = `${item?.draftBottleGroupName ?? ''}`.trim();
        const isContainerItem = !!(item.containerID || item.isContainer);

        return !groupName || !isContainerItem;
      });

      cart.forEach((item) => {
        const groupName = `${item?.draftBottleGroupName ?? ''}`.trim();

        if (!groupName) {
          return;
        }

        const groupKey = `${groupName}__${item?.draftBottleGroupSize ?? ''}`;
        const parsedRequestedQuantity = Number(item?.draftBottleGroupSize);
        const requestedQuantity = Number.isFinite(parsedRequestedQuantity) && parsedRequestedQuantity > 0
          ? parsedRequestedQuantity
          : Number(item?.noOfUnits) || 0;

        if (!groupedItemsMap.has(groupKey)) {
          groupedItemsMap.set(groupKey, {
            name: groupName,
            requestedQuantity,
            orderedContainers: [],
            quoteAnalysisRefID: item.quoteAnalysisRefID ?? null
          });
        }

        const group = groupedItemsMap.get(groupKey);

        if (!group) {
          return;
        }

        if (item.containerID || item.isContainer) {
          group.orderedContainers.push({
            containerID: item.containerID,
            requestedQuantity: requestedQuantity,
            comments: item.comments || '',
            multiplier: item.multiplier || 1,
          });
        }
      });

      quoteGroups.forEach((group: any) => {
        const groupKey = `${group.name}__${group.size ?? ''}`;

        if (!group.name || groupedItemsMap.has(groupKey)) {
          return;
        }

        groupedItemsMap.set(groupKey, {
          name: group.name,
          requestedQuantity: Number(group.size) || 0,
          orderedContainers: [],
          quoteAnalysisRefID: group.quoteAnalysisRefID ?? null
        });
      });

      const containers = topLevelItems.filter(item => item.containerID || item.isContainer);
      const packages = topLevelItems.filter(item => item.packageID || (!item.containerID && !item.isContainer));

      const orderedContainers = containers.map(item => ({
        containerID: item.containerID,
        requestedQuantity: (Number(item.noOfUnits) || 0),
        comments: item.comments || "",
        isBulkOrder: !!item.isBulkOrder,
        bulkType: item.bulkType || null,
        multiplier: item.multiplier || 1,
        bulkQuantity: item.bulkQuantity !== 'Individual' ? item.bulkQuantity : 0
      }));

      const orderedPackages = packages.map(item => ({
        packageID: item.packageID || item.id,
        requestedQuantity: (Number(item.noOfUnits) || 0),
        comments: item.comments || ""
      }));

      const bottleGroups = Array.from(groupedItemsMap.values()).map((group) => {
        return {
          name: group.name,
          requestedQuantity: group.requestedQuantity,
          orderedContainers: group.orderedContainers,
          quoteAnalysisRefID: group.quoteAnalysisRefID ?? null
        };
      });

      const transformedRecipient = {
        ...recipient,
        contactNo: currentContactNumber,
        email: recipient.emailId,
        PostalCode: recipient.postalCode,
        Country: recipient.country,
        specialInstruction: recipient.instructions,
        deliveryInstruction: recipient.deliveryInstruction,
        state: recipient.state
        //despatchLocationID: mode === 'delivery' ? recipient.despatchLocationID : recipient.pickUpLocationID
      };

      const clientId = selectedClientId;

      // Remove the old properties
      const { emailId, postalCode, country, instructions, saveForFuture, ...cleanRecipient } = transformedRecipient;

      const orderObj = {
        orderBy: labUserCheck?.primarysid,
        clientID: clientId,
        orderName: orderName.trim(),
        orderedOn: new Date().toISOString().split('T')[0],
        status: labUserCheck?.UserType === 'External' ? 1 : 2,
        needsDelivery: mode === 'delivery',
        comments: "",
        eskiesToFit: eskiesToFit,
        hasAttachments: attachments.length > 0,
        orderRecipient: {
          ...cleanRecipient,
          deliveryInstruction: recipient.deliveryInstruction || ''
        },
        orderedContainers,
        orderedPackages,
        bottleGroups,
        despatchLocationID: mode === 'delivery' ? recipient.despatchLocationID : recipient.pickUpLocationID
      };

      let apiCall = labUserCheck?.UserType === 'External' ? createOrder : createInternalUserOrder;

      const response = await apiCall(orderObj);

      if (response.status === 200) {
        const orderId: number = response.data?.id || response.data;

        // Upload attachments if any
        await uploadAttachmentsForOrder(orderId);

        setSnackbar({ open: true, message: 'Order placed successfully!', severity: 'success' });
        setAcknowledgedStatus(true);
        setAcknowledgeResponse(response.data);
        setAttachments([]);
        setCart([]);
        setEskiesToFit(false);
        if (labUserCheck?.UserType && saveForFuture) {
          await AddInternalUserAddress({ ...cleanRecipient, clientID: clientId });
        }
      } else {
        setSnackbar({ open: true, message: 'Order failed. Please try again.', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Order failed. Please try again.', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDespatchLocations = () => {
    return getInternalUserDespatchLocations().then((res: any) => {
      if (res?.status === 200) {
        setDispatchLocations(res?.data);
      } else {
        setDispatchLocations([]);
        setSnackbar({
          open: true,
          message: res.data || 'Failed to fetch dispatch locations',
          severity: 'error'
        });
      }
    }).catch(() => {
      setSnackbar({
        open: true,
        message: 'Failed to fetch dispatch locations',
        severity: 'error'
      });
      setDispatchLocations([]);
    });
  }

  useEffect(() => {
    setLoadingDrafts(true);
    const minDelay = new Promise(res => setTimeout(res, 600)); // 600ms minimum spinner
    Promise.all([getAllDraftCheckoutItems(), minDelay]).then(() => setLoadingDrafts(false));
  }, [selectedClientId]);

  const getAllDraftCheckoutItems = async () => {
    try {
      const response = await getAllDraftCheckOutListApi();
      if (response?.status === 200) {
        setDraftCheckoutItems(response?.data);
      } else {
        setDraftCheckoutItems([]);
      }
    } catch (error) {
      setDraftCheckoutItems([]);
    }
  };

  const handleDeleteAllApi = async (selectedClientId: any, mode: 'all' | 'drafted') => {
    try {
      const response = await deleteAllOrder(selectedClientId);
      if (response.status === 200) {
        setCart([]);
        setDraftedDeleteDialogOpen(false);
        setSnackbar({ open: true, message: mode === 'all' ? 'All items removed from cart' : 'Successfully removed from draft checkout items', severity: 'success' });
        if (mode === 'drafted') {
          await getAllDraftCheckoutItems();
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to remove items from cart', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to remove items from cart', severity: 'error' });
    }
  }

  const AddInternalUserAddress = async (details: any) => {
    try {
      let apiCall = labUserCheck?.UserType === 'External' ? addAddress : addInternalUserAddress;
      const response = await apiCall(details);
      if (response.status === 200) {
        setSnackbar({ open: true, message: 'Address added successfully!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Address addition failed. Please try again.', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Address addition failed. Please try again.', severity: 'error' });
    }
  }

  const times = [];
  for (let hour = 9; hour <= 18; hour++) {
    for (let minute of [0, 30]) {
      if (hour === 18 && minute === 30) continue;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const mm = String(minute).padStart(2, "0");
      times.push(`${hour12}:${mm} ${ampm}`);
    }
  }

  const requiredFieldStyles = {
    '& .MuiInputLabel-asterisk': {
      color: 'red',
    },
  };

  // Formats using the machine's local date parts (avoids toISOString's UTC shift near midnight)
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // After 5pm local time, tomorrow is no longer selectable - only the day after is
  const getRequiredDateCutoffMin = () => {
    const now = new Date();
    const daysToAdd = now.getHours() >= 17 ? 2 : 1;
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToAdd));
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderOrderSummaryStep();
      case 1:
        return renderOrderTypeStep();
      case 2:
        return renderConfirmationStep();
      default:
        return 'Unknown step';
    }
  };

  const columnsList = [
    { field: 'clientId', headerName: 'Client ID', minWidth: 200 },
    { field: 'clientName', headerName: 'Client Name', minWidth: 200 },
    { field: 'containersCount', headerName: 'Container Count', minWidth: 150 },
    { field: 'packagesCount', headerName: 'Packages Count', minWidth: 150 },
    { field: 'action', headerName: 'Action', minWidth: 100 }
  ];

  const renderQuoteOrderSection = () => orderSummarySections.quoteGroups.length > 0 && (
    <Box sx={{ mb: 6 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Order from Quote:
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', sm: '44% minmax(0, 1fr)' },
          gap: 2,
          py: 1,
          borderBottom: '1px solid #d7dce2'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003883' }}>
          Analysis
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003883' }}>
          Comments
        </Typography>
      </Box>
      {orderSummarySections.quoteGroups.map((group) => (
        <Box
          key={group.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', sm: '44% minmax(0, 1fr)' },
            gap: 2,
            py: 1,
            borderBottom: '1px solid #f0f0f0'
          }}
        >
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {group?.name}
          </Typography>
          <Tooltip title={group?.quoteAnalysisRefComments || ''} arrow placement="top">
            <Typography
              variant="body2"
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default',
              }}
            >
              {group?.quoteAnalysisRefComments}
            </Typography>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );

  const renderOrderSummaryStep = () => (
    <Paper variant="outlined" sx={{ p: 3 }}>
      {hasCheckoutItems && (
        <TextField
          label="Order Name"
          value={orderName}
          onChange={(e) => setOrderName(e.target.value)}
          fullWidth
          required
          size="small"
          error={
            orderName.trim() === '' ||
            exceedsMaxLength(orderName, FIELD_LIMITS.orderName)
          }
          helperText={
            orderName.trim() === ''
              ? 'Order name is required'
              : exceedsMaxLength(orderName, FIELD_LIMITS.orderName)
                ? maxLengthMessage(FIELD_LIMITS.orderName)
                : ''
          }
          sx={{ mb: 3 }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Order Summary
        </Typography>

        {hasCheckoutItems && (
          <FormControlLabel
            control={
              <Checkbox
                checked={eskiesToFit}
                onChange={(e) => setEskiesToFit(e.target.checked)}
                sx={{
                  color: '#003883',
                  '&.Mui-checked': {
                    color: '#003883',
                  },
                }}
              />
            }
            label="Eskies to fit"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontWeight: 500,
                color: '#2c3e50'
              }
            }}
          />
        )}
      </Box>

      {hasCheckoutItems && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2
          }}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={handleDeleteAllDialog}
          >
            Remove All
          </Button>
        </Box>
      )}

      {!hasCheckoutItems ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Your cart is empty. Please add some items to proceed.
          </Typography>
        </Box>
      ) : (
        <>
          {renderQuoteOrderSection()}
          {hasRegularOrderItems && <Box>
            {/* TABLE HEADER */}
            <Grid
              container
              spacing={1}
              sx={{
                px: 0,
                pb: 1,
                borderBottom: '1px solid #d7dce2',
                mb: 1.5
              }}
            >
              <Grid item xs={5} sm={5}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#003883'
                  }}
                >
                  Items
                </Typography>
              </Grid>

              <Grid item xs={2} sm={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#003883',
                    textAlign: 'center'
                  }}
                >
                  Multiplier
                </Typography>
              </Grid>

              <Grid item xs={2} sm={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#003883'
                  }}
                >
                  Unit Type
                </Typography>
              </Grid>

              <Grid item xs={2} sm={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#003883',
                    textAlign: 'center'
                  }}
                >
                  Qty
                </Typography>
              </Grid>

              <Grid item xs={1} sm={1} />
            </Grid>

            {/* UNGROUPED ITEMS */}
            {orderSummarySections.ungroupedItems.map((item) => {
              const isNoOfUnitsEmpty =
                `${item.noOfUnits ?? ''}`.trim() === '';

              return (
                <Box
                  key={item.containerID || item.id}
                  sx={{
                    py: 1,
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Grid
                    container
                    alignItems="center"
                    spacing={1}
                  >
                    {/* ITEM */}
                    <Grid item xs={5} sm={5}>
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {getCartItemLabel(item)}
                      </Typography>
                    </Grid>

                    {/* MULTIPLIER */}
                    <Grid
                      item
                      xs={2}
                      sm={2}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      {!item.isPackage && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid rgba(0,0,0,0.23)',
                            borderRadius: 1,
                            overflow: 'hidden',
                            height: 32
                          }}
                        >
                          <IconButton
                            size="small"
                            disabled={
                              item.bulkType !== 'Individual'
                            }
                            onClick={() =>
                              handleCartMultiplierChange(
                                item.id,
                                Math.max(
                                  1,
                                  getMultiplier(item) - 1
                                )
                              )
                            }
                            sx={{
                              borderRadius: 0,
                              height: '100%',
                              width: 24,
                              p: 0,
                              minWidth: 0
                            }}
                          >
                            <Remove sx={{ fontSize: 14 }} />
                          </IconButton>

                          <input
                            type="number"
                            value={getMultiplier(item)}
                            disabled={
                              item.bulkType !== 'Individual'
                            }
                            min={1}
                            onChange={(e) => {
                              const val = parseInt(
                                e.target.value,
                                10
                              );

                              if (!isNaN(val) && val >= 1) {
                                handleCartMultiplierChange(
                                  item.id,
                                  val
                                );
                              }
                            }}
                            style={{
                              width: 28,
                              textAlign: 'center',
                              border: 'none',
                              borderLeft:
                                '1px solid rgba(0,0,0,0.12)',
                              borderRight:
                                '1px solid rgba(0,0,0,0.12)',
                              outline: 'none',
                              fontSize: '12px',
                              height: '100%',
                              padding: 0,
                              WebkitAppearance: 'none',
                              MozAppearance: 'textfield'
                            }}
                          />

                          <IconButton
                            size="small"
                            disabled={
                              item.bulkType !== 'Individual'
                            }
                            onClick={() =>
                              handleCartMultiplierChange(
                                item.id,
                                getMultiplier(item) + 1
                              )
                            }
                            sx={{
                              borderRadius: 0,
                              height: '100%',
                              width: 24,
                              p: 0,
                              minWidth: 0
                            }}
                          >
                            <Add sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Grid>

                    {/* UNIT TYPE */}
                    <Grid item xs={2} sm={2}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {item.bulkType || '-'}
                      </Typography>
                    </Grid>

                    {/* QTY */}
                    <Grid item xs={2} sm={2}>
                      <TextField
                        type="number"
                        size="small"
                        value={item.noOfUnits ?? ''}
                        inputProps={{
                          min: 1,
                          style: {
                            width: 60,
                            textAlign: 'right'
                          }
                        }}
                        error={isNoOfUnitsEmpty}
                        onChange={(e) => {
                          const value = e.target.value;

                          const newQty =
                            value === ''
                              ? ''
                              : Math.max(0, Number(value));

                          handleQuantityChange(
                            item,
                            newQty
                          );
                        }}
                        onBlur={() =>
                          updateCartQuantities(item)
                        }
                      />
                    </Grid>

                    {/* DELETE */}
                    <Grid item xs={1}>
                      <Tooltip title="Remove item">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleDeleteItem(item)
                          }
                          sx={{
                            '&:hover': {
                              backgroundColor:
                                'rgba(211, 47, 47, 0.04)'
                            }
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

            {/* GROUPED ITEMS */}
            {orderSummarySections.groupedItems.map((group) => {
              const isGroupNoOfUnitsEmpty =
                `${group.size ?? ''}`.trim() === '';
              const hasGroupQuoteAnalysisRef = group.quoteAnalysisRefID != null;

              return (
                <Box
                  key={group.key}
                  sx={{ mt: hasGroupQuoteAnalysisRef ? 1 : 2.5 }}
                >
                  {/* GROUP HEADER */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: hasGroupQuoteAnalysisRef ? 0 : 1
                    }}
                  >
                    <Grid
                      container
                      alignItems="center"
                      spacing={1}
                    >
                      {/* GROUP NAME */}
                      <Grid item xs={hasGroupQuoteAnalysisRef ? 12 : 6.5} sm={hasGroupQuoteAnalysisRef ? 12 : 7}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: '#003883',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {group.name}
                        </Typography>
                      </Grid>

                      {/* EMPTY MULTIPLIER COLUMN */}
                      {!hasGroupQuoteAnalysisRef && <Grid item xs={2.5} sm={2} />}

                      {/* GROUP QTY */}
                      {!hasGroupQuoteAnalysisRef && (
                        <>
                          <Grid item xs={2} sm={2}>
                            <TextField
                              type="number"
                              size="small"
                              value={
                                group.items?.[0]?.noOfUnits ?? ''
                              }
                              inputProps={{
                                min: 1,
                                style: {
                                  width: 60,
                                  textAlign: 'right'
                                }
                              }}
                              error={isGroupNoOfUnitsEmpty}
                              onChange={(e) => {
                                const value =
                                  e.target.value;

                                const newQty =
                                  value === ''
                                    ? ''
                                    : Math.max(
                                      0,
                                      Number(value)
                                    );

                                // Update ALL cart items in group
                                const newCart = cart.map(
                                  (cartItem) =>
                                    group.items.some(
                                      (i) =>
                                        i.id === cartItem.id
                                    )
                                      ? {
                                        ...cartItem,
                                        noOfUnits: newQty
                                      }
                                      : cartItem
                                );

                                setCart(newCart);

                                // Sync API tracking
                                group.items.forEach(
                                  (i) => {
                                    setModifiedQuantities(
                                      (prev) => ({
                                        ...prev,
                                        [i.id]: newQty
                                      })
                                    );
                                  }
                                );
                              }}
                              onBlur={() => {
                                group.items.forEach(
                                  (item) => {
                                    updateCartQuantities(
                                      item
                                    );
                                  }
                                );
                              }}
                            />
                          </Grid>

                          {/* DELETE GROUP */}
                          <Grid item xs={1}>
                            <Tooltip title="Remove item">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteItem(group)
                                }
                                sx={{
                                  '&:hover': {
                                    backgroundColor:
                                      'rgba(211, 47, 47, 0.04)'
                                  }
                                }}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </>)}
                    </Grid>
                  </Box>

                  {/* GROUP SUB ITEMS */}
                  {!hasGroupQuoteAnalysisRef && group.items.map((item) => (
                    <Box
                      key={
                        item.containerID || item.id
                      }
                      sx={{
                        py: 1,
                        pl: {
                          xs: 0,
                          sm: 0
                        },
                        borderBottom:
                          '1px solid #f5f5f5'
                      }}
                    >
                      <Grid
                        container
                        alignItems="center"
                        spacing={1}
                      >
                        {/* ITEM */}
                        {!hasGroupQuoteAnalysisRef && (
                          <>
                            <Grid item xs={5} sm={5}>
                              <Typography variant="body2">
                                {getCartItemLabel(item)}
                              </Typography>
                            </Grid>

                            {/* MULTIPLIER */}
                            <Grid
                              item
                              xs={2}
                              sm={2}
                              sx={{
                                display: 'flex',
                                justifyContent:
                                  'center'
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  border:
                                    '1px solid rgba(0,0,0,0.23)',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  height: 32
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCartMultiplierChange(
                                      item.id,
                                      Math.max(
                                        1,
                                        getMultiplier(
                                          item
                                        ) - 1
                                      )
                                    )
                                  }
                                  sx={{
                                    borderRadius: 0,
                                    height: '100%',
                                    width: 24,
                                    p: 0,
                                    minWidth: 0
                                  }}
                                >
                                  <Remove
                                    sx={{
                                      fontSize: 14
                                    }}
                                  />
                                </IconButton>

                                <input
                                  type="number"
                                  value={getMultiplier(
                                    item
                                  )}
                                  min={1}
                                  onChange={(e) => {
                                    const val =
                                      parseInt(
                                        e.target.value,
                                        10
                                      );

                                    if (
                                      !isNaN(val) &&
                                      val >= 1
                                    ) {
                                      handleCartMultiplierChange(
                                        item.id,
                                        val
                                      );
                                    }
                                  }}
                                  style={{
                                    width: 28,
                                    textAlign:
                                      'center',
                                    border: 'none',
                                    borderLeft:
                                      '1px solid rgba(0,0,0,0.12)',
                                    borderRight:
                                      '1px solid rgba(0,0,0,0.12)',
                                    outline: 'none',
                                    fontSize: '12px',
                                    height: '100%',
                                    padding: 0,
                                    WebkitAppearance:
                                      'none',
                                    MozAppearance:
                                      'textfield'
                                  }}
                                />

                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCartMultiplierChange(
                                      item.id,
                                      getMultiplier(
                                        item
                                      ) + 1
                                    )
                                  }
                                  sx={{
                                    borderRadius: 0,
                                    height: '100%',
                                    width: 24,
                                    p: 0,
                                    minWidth: 0
                                  }}
                                >
                                  <Add
                                    sx={{
                                      fontSize: 14
                                    }}
                                  />
                                </IconButton>
                              </Box>
                            </Grid>

                            {/* UNIT TYPE */}
                            <Grid item xs={2} sm={2}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.bulkType || '-'}
                              </Typography>
                            </Grid>

                            {/* QTY */}
                            <Grid item xs={2} sm={2}>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: 'center'
                                }}
                              >
                                {item.noOfUnits ?? ''}
                              </Typography>
                            </Grid>
                          </>)}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>}

          {/* INTERNAL TOTAL */}
          {hasRegularOrderItems && <>
            <Divider sx={{ my: 2 }} />

            <Grid
              container
              alignItems="center"
              spacing={1}
              sx={{ pt: 0.5 }}
            >
              <Grid item xs={9} sm={9}>
                <Typography variant="h6">
                  Total Items:
                </Typography>
              </Grid>

              <Grid item xs={2} sm={2}>
                <Typography
                  variant="h6"
                  sx={{ textAlign: 'center' }}
                >
                  {totalItems}
                </Typography>
              </Grid>

              <Grid item xs={1} sm={1}>
                <Box />
              </Grid>
            </Grid>
          </>}
        </>
      )}
    </Paper>
  );

  const renderOrderTypeStep = () => (
    <Box>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">Order Type</FormLabel>
        <RadioGroup
          row
          value={mode}
          onChange={handleModeChange}
        >
          <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
          <FormControlLabel value="delivery" control={<Radio />} label="Delivery" />
        </RadioGroup>
      </FormControl>

      {(mode === 'pickup' || mode === 'delivery') && (
        <>
          {(mode === 'delivery' && localAddressList.length > 0) && (
            <Card sx={{ mb: 2 }}>
              <CardHeader
                title="Shipping Address"
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedAddress(defaultAddressId || selectedAddress);
                      setEditDialogOpen(true);
                    }}
                  >
                    Change
                  </Button>
                }
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                titleTypographyProps={{ fontWeight: 'bold' }}
                subheader={(() => {
                  let addr = localAddressList.find(a => a.id === defaultAddressId);
                  if (!addr && selectedAddress) {
                    addr = localAddressList.find(a => a.id === selectedAddress);
                  }
                  if (!addr) return 'No address selected';

                  return (
                    <div style={{ marginTop: '16px' }}>
                      <Typography variant='subtitle1'>{addr.name}</Typography>
                      <Typography variant='subtitle1'>{addr.suburb}, {addr.state}, {addr.country}, {addr.postalCode}</Typography>
                      <Typography variant='subtitle1'>{addr.contactNo}</Typography>
                      <Typography variant='subtitle1'>{addr.addedBy || addr.email}</Typography>
                    </div>
                  );
                })()}
              />
              <CardContent>
                {(localAddressList.length > 0) && <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      const today = new Date();
                      const todayDate = today.toISOString().split('T')[0];

                      setRecipient({
                        ...initialRecipient,
                        date: todayDate,
                        country: 'Australia'
                      });
                      setSearchContactName('');
                      setInternalContacts([]);
                      setIsAddingNewAddress(true);
                      setDateError('');
                      setContactNoError('');
                    }}
                  >
                    Add New Address
                  </Link>
                </Box>}
              </CardContent>
            </Card>
          )}

          {(mode === 'pickup' || mode === 'delivery') && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom mb={2}>
                {mode === 'pickup'
                  ? 'Contact Details'
                  : (isAddingNewAddress || !defaultAddressId || (addressList.length === 0) ? 'Add New Address' : 'Edit Address')
                }
              </Typography>
              <Grid container spacing={2}>
                {(labUserCheck?.UserType !== 'External' && mode === 'delivery') && <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Dispatch Location"
                    name="despatchLocationID"
                    size="small"
                    value={recipient.despatchLocationID || ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    sx={requiredFieldStyles}
                  >
                    {dispatchLocations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Business Name"
                    name="name"
                    size="small"
                    value={recipient.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={exceedsMaxLength(recipient.name, FIELD_LIMITS.recipientName)}
                    helperText={exceedsMaxLength(recipient.name, FIELD_LIMITS.recipientName) ? maxLengthMessage(FIELD_LIMITS.recipientName) : ''}
                    sx={requiredFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {!isExternalUser ?
                    <Autocomplete
                      freeSolo
                      open={open}
                      onOpen={() => setOpen(true)}
                      onClose={() => setOpen(false)}
                      options={internalContacts ?? []}
                      filterOptions={(x) => x}
                      loading={loading}
                      includeInputInList
                      value={selectedContact}
                      inputValue={searchContactName}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : getContactOptionLabel(option)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onInputChange={(_, value, reason) => {
                        if (reason === 'reset') return;

                        setSearchContactName(value);

                        setRecipient((prev) => ({
                          ...prev,
                          contactName: value,
                        }));

                        if (!value.trim()) {
                          setSelectedContact(null);
                          setOpen(true);
                        }
                      }}
                      onChange={(_, value) => {
                        if (!value) {
                          setSelectedContact(null);

                          setRecipient((prev) => ({
                            ...prev,
                            contactName: '',
                          }));

                          return;
                        }

                        if (typeof value === 'string') {
                          setSelectedContact(null);

                          setRecipient((prev) => ({
                            ...prev,
                            contactName: value,
                          }));

                          return;
                        }

                        setSelectedContact(value);

                        const contactName = getContactOptionLabel(value);

                        setSearchContactName(contactName);

                        setRecipient((prev) => ({
                          ...prev,
                          contactName: contactName,
                        }));

                        handleInternalContactSelect(value);
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2">
                              {getContactOptionLabel(option)}
                            </Typography>

                            {!!getContactEmail(option) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {getContactEmail(option)}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      noOptionsText={
                        searchContactName
                          ? 'No contacts found'
                          : 'Start typing to search'
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contact Name"
                          name="contactName"
                          size="small"
                          fullWidth
                          required
                          inputProps={{
                            ...params.inputProps
                          }}
                          error={exceedsMaxLength(searchContactName, FIELD_LIMITS.contactName)}
                          helperText={exceedsMaxLength(searchContactName, FIELD_LIMITS.contactName) ? maxLengthMessage(FIELD_LIMITS.contactName) : ''}
                          sx={requiredFieldStyles}
                        />
                      )}
                    /> :
                    <TextField
                      label="Contact Name"
                      name="contactName"
                      size="small"
                      value={recipient.contactName}
                      onChange={handleChange}
                      fullWidth
                      required
                      sx={requiredFieldStyles}
                      error={exceedsMaxLength(recipient.contactName, FIELD_LIMITS.contactName)}
                      helperText={exceedsMaxLength(recipient.contactName, FIELD_LIMITS.contactName) ? maxLengthMessage(FIELD_LIMITS.contactName) : ''}
                    />
                  }
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Contact Number"
                    name="contactNo"
                    size="small"
                    value={isExternalUser ? recipient.contactNo : currentContactNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d\s+()]/g, '');

                      handleContactNumberChange(value);

                      setContactNoError(
                        value.replace(/\D/g, '').length > 20
                          ? 'Contact number must not exceed 20 digits'
                          : ''
                      );
                    }}
                    fullWidth
                    required
                    sx={requiredFieldStyles}
                    error={!!contactNoError || exceedsMaxLength(currentContactNumber, FIELD_LIMITS.contactNo)}
                    helperText={contactNoError || (exceedsMaxLength(currentContactNumber, FIELD_LIMITS.contactNo) ? maxLengthMessage(FIELD_LIMITS.contactNo) : '')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    name="emailId"
                    size="small"
                    value={recipient.emailId}
                    onChange={handleChange}
                    fullWidth
                    required
                    type="email"
                    sx={requiredFieldStyles}
                    error={(!!recipient.emailId && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recipient.emailId)) || exceedsMaxLength(recipient.emailId, FIELD_LIMITS.email)}
                    helperText={
                      !!recipient.emailId && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recipient.emailId)
                        ? "Please enter a valid email address"
                        : exceedsMaxLength(recipient.emailId, FIELD_LIMITS.email)
                          ? maxLengthMessage(FIELD_LIMITS.email)
                          : ""
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={mode === 'pickup' ? 3 : 6}>
                  <DateInputField
                    label="Required Date"
                    value={recipient.date || ''}
                    onChange={(iso) => handleChange({ target: { name: 'date', value: iso } } as any)}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={requiredFieldStyles}
                    error={!!dateError}
                    helperText={dateError}
                    min={
                      labUserCheck?.UserType === 'Internal'
                        ? formatLocalDate(new Date())
                        : getRequiredDateCutoffMin()
                    }
                  />
                </Grid>
                {mode === 'pickup' && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        select
                        label="Pickup Location"
                        name="pickupLocation"
                        size="small"
                        required
                        sx={requiredFieldStyles}
                        value={recipient?.pickupLocation}
                        onChange={handleChange}
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                                overflowY: 'auto',
                              },
                            },
                          },
                        }}
                      >
                        {labLocations.map((location) => (
                          <MenuItem key={location.id} value={location.name}>
                            {location.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Grid>
                )}

                {mode === 'pickup' && recipient?.pickupLocation && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Building Number/Street"
                        name="street"
                        size="small"
                        value={recipient.street}
                        onChange={handleChange}
                        fullWidth
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Suburb"
                        name="suburb"
                        size="small"
                        value={recipient.suburb}
                        onChange={handleChange}
                        fullWidth
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="State"
                        name="state"
                        size="small"
                        value={getAllState.find(s => s.name === recipient.state)?.name || recipient.state}
                        onChange={handleChange}
                        fullWidth
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Postal Code"
                        name="postalCode"
                        size="small"
                        value={recipient.postalCode}
                        onChange={handleChange}
                        fullWidth
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        label="Country"
                        name="country"
                        size="small"
                        value={recipient.country}
                        onChange={handleChange}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </>
                )}

                {mode === 'delivery' && (
                  <>
                    <Grid item xs={6} sm={6}>
                      <TextField
                        label="Building Number/Street"
                        name="street"
                        size="small"
                        value={recipient.street}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={exceedsMaxLength(recipient.street, FIELD_LIMITS.street)}
                        helperText={exceedsMaxLength(recipient.street, FIELD_LIMITS.street) ? maxLengthMessage(FIELD_LIMITS.street) : ''}
                        sx={requiredFieldStyles}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <TextField
                        label="Suburb"
                        name="suburb"
                        size="small"
                        value={recipient.suburb}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={exceedsMaxLength(recipient.suburb, FIELD_LIMITS.suburb)}
                        helperText={exceedsMaxLength(recipient.suburb, FIELD_LIMITS.suburb) ? maxLengthMessage(FIELD_LIMITS.suburb) : ''}
                        sx={requiredFieldStyles}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <TextField
                        label="State"
                        name="state"
                        size="small"
                        value={getAllState.find(s => s.name === recipient.state)?.name || recipient.state}
                        onChange={handleChange}
                        fullWidth
                        required
                        select
                        sx={requiredFieldStyles}
                      >
                        {getAllState.map((state) => (
                          <MenuItem key={state.id} value={state.name}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <TextField
                        label="Postal Code"
                        name="postalCode"
                        size="small"
                        value={recipient.postalCode}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={exceedsMaxLength(recipient.postalCode, FIELD_LIMITS.postalCode)}
                        helperText={exceedsMaxLength(recipient.postalCode, FIELD_LIMITS.postalCode) ? maxLengthMessage(FIELD_LIMITS.postalCode) : ''}
                        sx={requiredFieldStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={labUserCheck?.UserType !== 'External' ? 6 : 12}>
                      <Tooltip title="Online ordering is only currently available for Australia" arrow>
                        <TextField
                          label="Country"
                          name="country"
                          size="small"
                          value="Australia"
                          onChange={(e) => {
                            setRecipient({ ...recipient, country: "Australia" });
                          }}
                          fullWidth
                          disabled
                          sx={{
                            '& .MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: '#000000',
                              color: '#000000'
                            }
                          }}
                        />
                      </Tooltip>
                    </Grid>
                    {mode === 'delivery' && !recipient.isDefault && (isAddingNewAddress || !defaultAddressId || addressList.length === 0) && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={recipient.saveForFuture || false}
                              onChange={e =>
                                setRecipient({ ...recipient, saveForFuture: e.target.checked })
                              }
                            />
                          }
                          label={<Typography variant="body2">Save for future use</Typography>}
                        />
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    label="Special Instructions"
                    name="instructions"
                    size="small"
                    value={recipient.instructions}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    error={exceedsMaxLength(recipient.instructions, FIELD_LIMITS.instructions)}
                    helperText={exceedsMaxLength(recipient.instructions, FIELD_LIMITS.instructions) ? maxLengthMessage(FIELD_LIMITS.instructions) : characterCountText(recipient.instructions, FIELD_LIMITS.instructions)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Delivery Instructions"
                    name="deliveryInstruction"
                    size="small"
                    value={recipient.deliveryInstruction}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    error={exceedsMaxLength(recipient.deliveryInstruction, FIELD_LIMITS.deliveryInstruction)}
                    helperText={exceedsMaxLength(recipient.deliveryInstruction, FIELD_LIMITS.deliveryInstruction) ? maxLengthMessage(FIELD_LIMITS.deliveryInstruction) : characterCountText(recipient.deliveryInstruction, FIELD_LIMITS.deliveryInstruction)}
                  />
                </Grid>
              </Grid>

              {mode === 'delivery' && isAddingNewAddress && addressList.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setIsAddingNewAddress(false);
                      if (defaultAddressId) {
                        const addr = addressList.find(a => a.id === defaultAddressId);
                        if (addr) {
                          const newRecipient = {
                            name: addr.name,
                            emailId: getRecipientEmail(addr.emailId || addr.email || ''),
                            contactName: defaultRecipientContactName,
                            ...getContactNumberFields(addr),
                            date: addr.date || '',
                            suburb: addr.suburb,
                            state: addr.state || '',
                            street: addr.street || '',
                            postalCode: addr.postalCode || addr.postalCode,
                            country: addr.country || 'Australia',
                            pickupLocation: addr.pickupLocation || '',
                            pickUpLocationID: addr.pickUpLocationID || '',
                            instructions: addr.instructions || '',
                            deliveryInstruction: addr.deliveryInstruction || '',
                            saveForFuture: false,
                            isDefault: addr.isDefault || false,
                            despatchLocationID: addr.despatchLocationID || ''
                          };
                          setRecipient(newRecipient);
                          setSearchContactName('');
                          setInternalContacts([]);
                          validateDate(newRecipient.date);
                          setContactNoError('');
                        }
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {mode === 'delivery' && (
            <Dialog
              open={editDialogOpen}
              onClose={() => {
                setEditDialogOpen(false);
                setSelectedAddress(defaultAddressId);
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Select Delivery Address ({addressList.length} addresses available)</DialogTitle>
              <DialogContent>
                <MuiRadioGroup
                  value={selectedAddress}
                  onChange={e => {
                    let selectedId = Number(e.target.value);
                    setSelectedAddress(selectedId);
                  }}
                >
                  {addressList.map(addr => {
                    return (
                      <MuiFormControlLabel
                        key={addr.id}
                        value={addr.id}
                        control={<MuiRadio />}
                        label={
                          <Box sx={{ py: 1 }}>
                            <Typography variant="subtitle2">
                              {addr.name} {addr.isDefault && <span style={{ color: 'green' }}>(Default)</span>}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {addr.userName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {addr.street}, {addr.suburb}, {addr.state}, {addr.country}, {addr.postalCode}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          margin: 0,
                          padding: 1,
                          borderBottom: '1px solid #f0f0f0',
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      />
                    );
                  })}
                </MuiRadioGroup>

                {addressList.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No addresses found. Please add a new address.
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                  <Box>
                    <Button
                      onClick={() => {
                        setEditDialogOpen(false);
                        setSelectedAddress(defaultAddressId);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (selectedAddress) {
                          const addr = addressList.find(a => a.id == selectedAddress);
                          if (addr) {
                            const newRecipient = {
                              name: addr.name,
                              emailId: getRecipientEmail(addr.emailId || addr.email || ''),
                              contactName: defaultRecipientContactName,
                              ...getContactNumberFields(addr),
                              date: addr.date || '',
                              suburb: addr.suburb,
                              state: addr.state || '',
                              street: addr.street || '',
                              postalCode: addr.postalCode || '',
                              country: addr.country || 'Australia',
                              pickupLocation: addr.pickupLocation || '',
                              instructions: addr.instructions || '',
                              deliveryInstruction: addr.deliveryInstruction || '',
                              pickUpLocationID: addr.pickUpLocationID || '',
                              saveForFuture: false,
                              isDefault: addr.isDefault || false,
                              despatchLocationID: addr.despatchLocationID || ''
                            };

                            setRecipient(newRecipient);
                            setSearchContactName('');
                            setInternalContacts([]);

                            validateDate(newRecipient.date);

                            setDefaultAddressId(selectedAddress);
                            setContactNoError('');
                          }
                        }
                        setIsAddingNewAddress(false);
                        setEditDialogOpen(false);
                      }}
                      variant="contained"
                      size="small"
                      disabled={!selectedAddress}
                    >
                      Select Address
                    </Button>
                  </Box>
                </Box>
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
    </Box>
  );

  const renderConfirmationStep = () => (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Review Your Order
      </Typography>

      {renderQuoteOrderSection()}

      <Box sx={{ mb: 3 }}>
        {hasRegularOrderItems && <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Order Items:
        </Typography>}
        <>
          {hasRegularOrderItems && <Box sx={{ pl: 0 }}>
            <Grid container spacing={1} sx={{ px: 0, pb: 1, borderBottom: '1px solid #d7dce2', mb: 1.5 }}>
              <Grid item xs={6} sm={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003883' }}>
                  Items
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003883' }}>
                  Multiplier
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003883' }}>
                  Unit Type
                </Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003883', textAlign: 'center' }}>
                  QTY
                </Typography>
              </Grid>
            </Grid>

            {orderSummarySections.ungroupedItems.map((item) => (
              <Box key={item.containerID || item.id} sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={6} sm={6}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {getCartItemLabel(item)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    <Typography variant="body2" color="text.secondary">
                      {!item.isPackage ? item.multiplier : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    <Typography variant="body2" color="text.secondary">
                      {item.bulkType || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      {item.noOfUnits}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {orderSummarySections.groupedItems.map((group) => {
              const hasGroupQuoteAnalysisRef = group.quoteAnalysisRefID != null;

              return <Box key={group.key} sx={{ mt: hasGroupQuoteAnalysisRef ? 1 : 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#003883', whiteSpace: 'nowrap' }}>
                    {group.name}
                  </Typography>
                  <Box sx={{ flex: 1, borderBottom: '1px solid #003883', opacity: 0.65 }} />
                </Box>
                {!hasGroupQuoteAnalysisRef && group.items.map((item) => (
                  <Box key={item.containerID || item.id} sx={{ py: 1, pl: { xs: 0, sm: 0 }, borderBottom: '1px solid #f5f5f5' }}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={6} sm={6}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                          {getCartItemLabel(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Typography variant="body2" color="text.secondary">
                          {item.multiplier || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Typography variant="body2" color="text.secondary">
                          {item.bulkType || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} sm={2}>
                        <Typography variant="body2" sx={{ textAlign: 'center' }}>
                          {item.noOfUnits}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>;
            })}

          </Box>}
          {hasRegularOrderItems && <Grid container alignItems="center" spacing={1} sx={{ mt: 2, pt: 2, borderTop: '2px solid #ddd' }}>
            <Grid item xs={9.5} sm={9.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Total Items:</Typography>
            </Grid>
            <Grid item xs={2.5} sm={2.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{totalItems}</Typography>
            </Grid>
          </Grid>}
        </>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Order Type Section */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0 }}>
          Order Type:
        </Typography>
        <Box sx={{ display: 'inline-block', backgroundColor: mode === 'delivery' ? '#EE7D11' : '#003883', color: '#fff', padding: '6px 12px', borderRadius: '20px' }}>
          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>{mode}</Typography>
        </Box>
      </Box>
      {/* Contact Details Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Contact Details:
        </Typography>
        <Box sx={{ pl: 2 }}>
          {labUserCheck?.UserType !== 'External' && <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Dispatch Location: </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{dispatchLocations.find(loc => loc.id === recipient.despatchLocationID)?.name || recipient.despatchLocationID}</Typography>
          </Box>}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Business Name:</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Email:</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.emailId}</Typography>
          </Box>
          {recipient.contactName && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Contact Name:</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.contactName}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Contact Number:</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{isExternalUser ? recipient.contactNo : currentContactNumber}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Required Date:</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{new Date(recipient.date).toLocaleDateString('en-AU', { month: '2-digit', day: '2-digit', year: 'numeric' })}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Pickup/Delivery Details */}
      <Box sx={{ mb: 3 }}>
        {mode === 'pickup' && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Pickup Details:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Pickup Location:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.pickupLocation}</Typography>
              </Box>
            </Box>
          </>
        )}
        {mode === 'delivery' && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Delivery Address:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Street Address:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.street}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Suburb:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.suburb}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>State:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.state}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Postal Code:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.postalCode}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Country:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', pl: 2 }}>{recipient.country}</Typography>
              </Box>
            </Box>
          </>
        )}
        {(recipient.instructions || recipient.deliveryInstruction) && (
          <Box sx={{ pl: 2, mt: mode === 'delivery' || mode === 'pickup' ? 2 : 0 }}>
            {recipient.instructions && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Special Instructions:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', textAlign: 'right', pl: 2, maxWidth: '60%' }}>{recipient.instructions}</Typography>
              </Box>
            )}
            {recipient.deliveryInstruction && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>Delivery Instructions:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', textAlign: 'right', pl: 2, maxWidth: '60%' }}>{recipient.deliveryInstruction}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {attachments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Attachments:
          </Typography>
          <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {attachments.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  px: 1.5,
                  py: 1,
                  border: '1px solid #d0d9f0',
                  borderRadius: 1.5,
                  backgroundColor: '#fff'
                }}
              >
                <Typography variant="body2" sx={{ wordBreak: 'break-all', color: '#2c3e50' }}>
                  {file.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    whiteSpace: 'nowrap',
                    color: '#fff',
                    backgroundColor: '#003883',
                    borderRadius: 10,
                    px: 1,
                    py: 0.25,
                    fontSize: '0.68rem',
                    fontWeight: 500,
                  }}
                >
                  {file.size < 1024 * 1024
                    ? `${(file.size / 1024).toFixed(1)} KB`
                    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Terms and Conditions */}
      <Box
        sx={{
          mt: 2,
          px: 2,
          py: 1.5,
          backgroundColor: '#f5f8fc',
          borderRadius: '8px',
          border: '1px solid #dbe5f0',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#555',
            fontSize: '12px',
          }}
        >
          I have read and agree to the{' '}
          <a
            href="https://www.eurofins.com/en-au/environment-testing/resources/terms-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#003883',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Terms & Conditions
          </a>
          .
        </Typography>
      </Box>

    </Paper>
  );

  return (
    <div style={{ padding: '0px 24px', maxWidth: 'auto' }}>
      {/* Draft Checkout Items Table - only show if no client selected */}
      {(selectedClientId === -1) ? (
        loadingDrafts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          draftCheckoutItems && draftCheckoutItems.length > 0 ? (
            <Box sx={{ margin: 'auto', mb: 4 }}>
              <Typography variant="h6" gutterBottom>Draft Checkout Items</Typography>
              <Paper variant="outlined" sx={{ p: 0 }}>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead sx={{ backgroundColor: 'rgb(0, 56, 131)' }}>
                    <TableRow>
                      {columnsList.map(col => (
                        <TableCell key={col.field} sx={{ color: '#fff', minWidth: col.minWidth, fontWeight: 600 }}>
                          {col.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draftCheckoutItems?.map((row) => (
                      <TableRow key={row?.clientId}>
                        <TableCell>{row?.clientId}</TableCell>
                        <TableCell>{row?.clientName || '-'}</TableCell>
                        <TableCell>{row?.containersCount || '-'}</TableCell>
                        <TableCell>{row?.packagesCount || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setSelectedClientId(row?.clientId, row?.clientName || '')}
                          >
                            View
                          </Button> &nbsp;
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              handleDraftedDeleteItem(row);
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <Typography>No  found.</Typography>
            </Box>
          )
        )
      ) : (
        <>
          {/* Add Back to Drafts button */}
          {labUserCheck?.UserType !== 'External' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setSelectedClientId(-1);
                  updateClientSelected(false);
                  setCart([]); // Clear cart when going back to draft selection
                }}
              >
                Back to Drafted Checkout Items
              </Button>
            </Box>
          )}
          {/* ...existing code for checkout flow... */}
          {acknowledgedStatus ? (
            <div style={{ textAlign: 'center', padding: '20px', lineHeight: '1.5' }}>
              <img src={ContainerImage} alt="Container" style={{ width: '100px', height: '100px', marginBottom: '20px' }} />
              <Typography variant="h6" gutterBottom>Thank you for your order!</Typography>
              <Typography variant="body1" gutterBottom>Order no: <b>#{acknowledgeResponse?.id}</b></Typography>
              <Typography variant="body1" gutterBottom>
                You will receive the next status email once your order is processed. If there are any changes to your order, please do not hesitate to contact your ASM for assistance.
              </Typography>
              <Typography variant="caption" gutterBottom mt={2}>
                <b>*</b> Please note that we require 24 hour's notice for all online order/48 hour's for regional address.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (labUserCheck?.UserType !== 'External') {
                      setSelectedClientId(-1); // Reset to draft selection
                    }
                    onSubmit({ cart, ...recipient, mode });
                    navigate(`/home/orderHistory?clientId=${selectedClientId}`);
                  }}
                  sx={{ minWidth: 220 }}
                >
                  Go to Order History
                </Button>
              </Box>
            </div>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                {`Checkout ${clientInformation?.name ? `(${clientInformation?.name ?? ''})` : ''}`}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ maxWidth: 800, margin: 'auto', pt: 2, pb: 3 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Box sx={{ mt: 2, mb: 2 }}>
                  {getStepContent(activeStep)}
                </Box>

                {activeStep === 0 && hasCheckoutItems && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003883' }}>
                        Attachments
                      </Typography>
                    </Box>

                    {/* Drop zone / upload trigger */}
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        py: 2.5,
                        border: '2px dashed #b0bec5',
                        borderRadius: 2,
                        backgroundColor: '#f9fbff',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        '&:hover': {
                          borderColor: '#003883',
                          backgroundColor: '#eef2fb',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#003883', fontWeight: 500 }}>
                        Click to upload files
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PDF, Excel, Word, CSV, Outlook, Images &nbsp;·&nbsp; Max total 2 MB
                      </Typography>
                    </Box>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ALLOWED_ACCEPT}
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const incoming = Array.from(e.target.files || []);
                        const accepted: File[] = [];
                        const rejectedType: string[] = [];
                        const rejectedDuplicate: string[] = [];
                        let runningTotal = attachments.reduce((sum, f) => sum + f.size, 0);
                        const rejectedSize: string[] = [];

                        for (const file of incoming) {
                          if (!isAllowedFileType(file)) {
                            rejectedType.push(file.name);
                            continue;
                          }
                          if (attachments.some(f => f.name === file.name)) {
                            rejectedDuplicate.push(file.name);
                            continue;
                          }
                          if (runningTotal + file.size > MAX_TOTAL_SIZE) {
                            rejectedSize.push(file.name);
                            continue;
                          }
                          accepted.push(file);
                          runningTotal += file.size;
                        }

                        const messages: string[] = [];
                        if (rejectedType.length > 0) {
                          messages.push(`Unsupported file type: ${rejectedType.join(', ')}`);
                        }
                        if (rejectedSize.length > 0) {
                          messages.push(`Total upload limit is 2 MB. ${rejectedSize.join(', ')} ${rejectedSize.length === 1 ? 'was' : 'were'} not added.`);
                        }
                        if (rejectedDuplicate.length > 0) {
                          messages.push(`Already added: ${rejectedDuplicate.join(', ')}`);
                        }
                        if (messages.length > 0) {
                          setSnackbar({ open: true, message: messages.join(' | '), severity: 'error' });
                        }
                        if (accepted.length > 0) {
                          setAttachments(prev => [...prev, ...accepted]);
                        }
                        e.target.value = '';
                      }}
                    />

                    {attachments.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {attachments.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                backgroundColor: '#fff',
                                border: '1px solid #d0d9f0',
                                borderRadius: 1.5,
                                boxShadow: '0 1px 3px rgba(0,56,131,0.06)',
                              }}
                            >
                              <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', color: '#2c3e50' }}>
                                {file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  color: '#fff',
                                  backgroundColor: '#003883',
                                  borderRadius: 10,
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '0.68rem',
                                  fontWeight: 500,
                                }}
                              >
                                {file.size < 1024 * 1024
                                  ? `${(file.size / 1024).toFixed(1)} KB`
                                  : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                              </Typography>
                              <Tooltip title="Remove file">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                                  sx={{ ml: 0.5 }}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>

                        {/* Summary row */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1,
                            px: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {attachments.length} {attachments.length === 1 ? 'file' : 'files'} &nbsp;·&nbsp; Total:{' '}
                            {(() => {
                              const totalBytes = attachments.reduce((sum, f) => sum + f.size, 0);
                              return totalBytes < 1024 * 1024
                                ? `${(totalBytes / 1024).toFixed(1)} KB`
                                : `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`;
                            })()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Max total: 2 MB
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {activeStep === 0 && hasCheckoutItems && showQualityPackagePrompt && !hasPackageInCart && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 3, alignItems: 'center' }}
                    action={
                      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            onClose?.();
                            navigate(`/home/bottleOrderList?clientId=${selectedClientId}`, {
                              state: {
                                activeTab: 1
                              },
                            });
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setHasDeclinedQualityPackagePrompt(true);
                            setShowQualityPackagePrompt(false);
                          }}
                        >
                          No
                        </Button>
                      </Box>
                    }
                  >
                    Do you want to add quality and package?
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, mb: 5 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                  <Box>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isConfirmDisabled || isSubmitting}
                      >
                        Confirm Order
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={
                          (activeStep === 0 && !canProceedToStep2) ||
                          (activeStep === 1 && (
                            !canProceedToStep3 ||
                            ((isAddingNewAddress || !defaultAddressId) && (
                              !recipient.name ||
                              !recipient.emailId ||
                              !currentContactNumber ||
                              !recipient.date ||
                              !recipient.state ||
                              !recipient.street ||
                              !recipient.suburb ||
                              !recipient.postalCode ||
                              !recipient.country ||
                              (!isExternalUser && !recipient.contactName) ||
                              !!dateError ||
                              !isEmailValid ||
                              !!contactNoError
                            ))
                          ))
                        }
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {deleteDialogMode === 'all' ? 'Remove All Items from Cart' : 'Remove Item from Cart'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {deleteDialogMode === 'all'
              ? 'Are you sure you want to remove all items from your cart?'
              : <>Are you sure you want to remove <b>{itemToDelete?.containerName}</b> from your cart?</>
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteItem}
            color="primary"
            variant="contained"
            autoFocus
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={draftedDeleteDialogOpen}
        onClose={() => {
          setDraftedDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Remove from Draft Checkout Items
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove <b>{itemToDelete?.clientName}</b> from your draft checkout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDraftedDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteAllApi(itemToDelete?.clientId, 'drafted')}
            color="primary"
            variant="contained"
            autoFocus
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          <b>{snackbar.message}</b>
        </Alert>
      </Snackbar>
    </div>
  );
}
